'use strict'

import {
  roundToTenth,
  toFiniteNumber
} from './portal-geo-utils.js'
import {
  buildKmzScopedAltitudePropsFromSources,
  applyKmzScopedAltitudeProps,
  hasKmzAltitudeContextScopedProps,
  resolveKmzMissionAltitudeContext,
  resolveKmzPlaybackUseGlobalHeight
} from './portal-kmz-altitude-utils.js'

const _ = window._

const DEFAULT_POINT_HEIGHT_PATH_ALIASES = [
  'Point.height',
  'wpml:height',
  'height',
  'template.wpml:height',
  'waylines.wpml:height'
]

const DEFAULT_POINT_EXECUTE_HEIGHT_PATH_ALIASES = [
  'Point.executeHeight',
  'wpml:executeHeight',
  'executeHeight',
  'template.wpml:executeHeight',
  'waylines.wpml:executeHeight'
]

const DEFAULT_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES = [
  'wpml:ellipsoidHeight',
  'ellipsoidHeight',
  'template.wpml:ellipsoidHeight',
  'waylines.wpml:ellipsoidHeight'
]

const getPointHeightPathAliases = function (ctx) {
  return (ctx && Array.isArray(ctx.POINT_HEIGHT_PATH_ALIASES) && ctx.POINT_HEIGHT_PATH_ALIASES.length)
    ? ctx.POINT_HEIGHT_PATH_ALIASES
    : DEFAULT_POINT_HEIGHT_PATH_ALIASES
}

const getPointExecuteHeightPathAliases = function (ctx) {
  return (ctx && Array.isArray(ctx.POINT_EXECUTE_HEIGHT_PATH_ALIASES) && ctx.POINT_EXECUTE_HEIGHT_PATH_ALIASES.length)
    ? ctx.POINT_EXECUTE_HEIGHT_PATH_ALIASES
    : DEFAULT_POINT_EXECUTE_HEIGHT_PATH_ALIASES
}

const getPointEllipsoidHeightPathAliases = function (ctx) {
  return (ctx && Array.isArray(ctx.POINT_ELLIPSOID_HEIGHT_PATH_ALIASES) && ctx.POINT_ELLIPSOID_HEIGHT_PATH_ALIASES.length)
    ? ctx.POINT_ELLIPSOID_HEIGHT_PATH_ALIASES
    : DEFAULT_POINT_ELLIPSOID_HEIGHT_PATH_ALIASES
}

const buildDemLine = function (providerKey, elevationMeters) {
  const elevationText = Number(elevationMeters).toFixed(1) + ' m'
  return String(providerKey || '').trim() + ': ' + elevationText
}

const formatMeters = function (value) {
  const n = Math.round(Number(value) * 10) / 10
  if (!Number.isFinite(n)) return ''
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n))
  return n.toFixed(1)
}

const buildAglLine = function (providerKey, demMeters, altitudeMeters, aglMeters) {
  const demText = formatMeters(demMeters)
  const altText = formatMeters(altitudeMeters)
  const aglText = formatMeters(aglMeters)
  return String(providerKey || '').trim() + '(' + demText + ' m)+Alt(' + altText + ' m)=' + aglText + ' m'
}

const getPointDemValues = function (point) {
  const values = _.get(point, 'props.__demValues')
  if (!values || typeof values !== 'object') return {}
  return values
}

const getPointXmlNodes = function (point, ctx) {
  if (ctx && typeof ctx.fnGetPointXmlNodes === 'function') {
    return ctx.fnGetPointXmlNodes(point)
  }
  if (!point) return []
  const nodes = []
  if (point.xmlEl) nodes.push(point.xmlEl)
  const linked = Array.isArray(point.linkedXmlEls) ? point.linkedXmlEls : []
  linked.forEach((node) => {
    if (node) nodes.push(node)
  })
  return nodes
}

const getPreferredPointDemValue = function (point, ctx) {
  const values = getPointDemValues(point)
  const providerPriority = (ctx && Array.isArray(ctx.DEM_PROVIDER_PRIORITY)) ? ctx.DEM_PROVIDER_PRIORITY : []
  const isDemProviderEnabled = (ctx && typeof ctx.fnIsDemProviderEnabled === 'function')
    ? ctx.fnIsDemProviderEnabled
    : function () { return true }
  for (let i = 0; i < providerPriority.length; i++) {
    const providerKey = providerPriority[i]
    const val = Number(values[providerKey])
    if (Number.isFinite(val)) return val
  }
  const fallbackKeys = Object.keys(values)
  for (let i = 0; i < fallbackKeys.length; i++) {
    const providerKey = String(fallbackKeys[i] || '').trim()
    if (!isDemProviderEnabled(providerKey)) continue
    const val = Number(values[providerKey])
    if (Number.isFinite(val)) return val
  }
  return null
}

const getFirstNumericFromPointXmlTags = function (point, tagNames, ctx) {
  const nodes = getPointXmlNodes(point, ctx)
  for (let n = 0; n < nodes.length; n++) {
    const node = nodes[n]
    if (!node || !Array.isArray(tagNames)) continue
    for (let i = 0; i < tagNames.length; i++) {
      const tag = String(tagNames[i] || '').trim()
      if (!tag) continue
      const el = node.getElementsByTagName(tag)[0]
      if (!el) continue
      const val = toFiniteNumber(String(el.textContent || '').trim())
      if (val !== null) return val
    }
  }
  return null
}

const getCoordinateAltFromPointXml = function (point, ctx) {
  const nodes = getPointXmlNodes(point, ctx)
  for (let n = 0; n < nodes.length; n++) {
    const node = nodes[n]
    if (!node) continue
    const pointEl = node.getElementsByTagName('Point')[0]
    if (!pointEl) continue
    const coordEl = pointEl.getElementsByTagName('coordinates')[0]
    if (!coordEl) continue
    const raw = String(coordEl.textContent || '').trim()
    if (!raw) continue
    const firstChunk = raw.split(/\s+/)[0]
    const parts = firstChunk.split(',')
    if (parts.length < 3) continue
    const alt = toFiniteNumber(parts[2])
    if (alt !== null) return alt
  }
  return null
}

const getPointNumericByPathAliases = function (point, aliases) {
  const props = (point && point.props && typeof point.props === 'object') ? point.props : {}
  for (let i = 0; i < aliases.length; i++) {
    const num = toFiniteNumber(_.get(props, aliases[i]))
    if (num !== null) return num
  }
  return null
}

const getPointLatLng = function (point) {
  if (point && Array.isArray(point.latlng) && point.latlng.length >= 2) {
    const latFromArray = toFiniteNumber(point.latlng[0])
    const lngFromArray = toFiniteNumber(point.latlng[1])
    if (latFromArray !== null && lngFromArray !== null) {
      return { lat: latFromArray, lng: lngFromArray }
    }
  }
  const props = (point && point.props && typeof point.props === 'object') ? point.props : {}
  const lat = toFiniteNumber(_.get(props, 'coordinates.lat'))
  const lng = toFiniteNumber(_.get(props, 'coordinates.lng'))
  if (lat !== null && lng !== null) {
    return { lat: lat, lng: lng }
  }
  return null
}

const getConfiguredEgm96OffsetFallback = function (point, ctx) {
  const rules = (ctx && ctx.ALTITUDE_RULES && typeof ctx.ALTITUDE_RULES === 'object') ? ctx.ALTITUDE_RULES : {}
  const defaultOffset = toFiniteNumber(rules.egm96FallbackDefaultOffset)
  const grid = Array.isArray(rules.egm96HungaryFallbackGrid) ? rules.egm96HungaryFallbackGrid : []
  const latlng = getPointLatLng(point)
  if (!latlng) return defaultOffset
  for (let i = 0; i < grid.length; i++) {
    const cell = grid[i] || {}
    const lonMin = toFiniteNumber(cell.lonMin)
    const lonMax = toFiniteNumber(cell.lonMax)
    const latMin = toFiniteNumber(cell.latMin)
    const latMax = toFiniteNumber(cell.latMax)
    const offset = toFiniteNumber(cell.offset)
    if (lonMin === null || lonMax === null || latMin === null || latMax === null || offset === null) continue
    if (latlng.lng < lonMin || latlng.lng > lonMax) continue
    if (latlng.lat < latMin || latlng.lat > latMax) continue
    return offset
  }
  return defaultOffset
}

const setPointValueByPathAliases = function (point, aliases, value, ctx) {
  if (!point || !Array.isArray(aliases) || !aliases.length) return false
  if (!point.props || typeof point.props !== 'object') point.props = {}
  let applied = false
  aliases.forEach((path) => {
    if (_.get(point.props, path) === undefined) return
    ctx.fnSetByPath(point.props, path, value)
    ctx.fnUpdatePointXmlByPath(point, path, value)
    applied = true
  })
  if (applied) return true
  const fallbackPath = aliases[0]
  ctx.fnSetByPath(point.props, fallbackPath, value)
  ctx.fnUpdatePointXmlByPath(point, fallbackPath, value)
  return true
}

const computeDefaultEgm96OffsetForPoints = function (points, ctx) {
  const list = Array.isArray(points) ? points : []
  const configuredOffsets = []
  list.forEach((pt) => {
    const offset = getConfiguredEgm96OffsetFallback(pt, ctx)
    if (offset !== null) configuredOffsets.push(offset)
  })
  if (configuredOffsets.length) {
    configuredOffsets.sort((a, b) => a - b)
    const mid = Math.floor(configuredOffsets.length / 2)
    if (configuredOffsets.length % 2 === 1) return configuredOffsets[mid]
    return (configuredOffsets[mid - 1] + configuredOffsets[mid]) / 2
  }
  return getConfiguredEgm96OffsetFallback(list[0] || null, ctx)
}

const getPointCachedEgm96Offset = function (point) {
  return toFiniteNumber(_.get(point, 'props.__egm96Offset'))
}

const setPointCachedEgm96Offset = function (point, offset) {
  if (!point) return false
  const n = toFiniteNumber(offset)
  if (n === null) return false
  if (!point.props || typeof point.props !== 'object') point.props = {}
  point.props.__egm96Offset = n
  return true
}

const syncPointCachedEgm96Offset = function (point, ctx) {
  const offset = getConfiguredEgm96OffsetFallback(point, ctx)
  if (offset === null) return false
  return setPointCachedEgm96Offset(point, offset)
}

const isEllipsoidHeightPath = function (path) {
  const p = String(path || '').toLowerCase()
  return p.includes('ellipsoidheight')
}

const isWaypointHeightPath = function (path, ctx) {
  const p = String(path || '').toLowerCase()
  if (!p) return false
  if (isEllipsoidHeightPath(p)) return false
  if (p === ctx.NON_EXPORT_POINT_HEIGHT_PATH) return false
  return p.endsWith('.height') || p === 'height' || p.includes('wpml:height')
}

const normalizeAltitudeMode = function (modeRaw, ctx) {
  const raw = String(modeRaw || '').trim()
  if (!raw) return ''
  const compact = raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
  const aliases = (ctx.ALTITUDE_RULES && ctx.ALTITUDE_RULES.modeAliases && typeof ctx.ALTITUDE_RULES.modeAliases === 'object')
    ? ctx.ALTITUDE_RULES.modeAliases
    : {}
  const canonicalKeys = Object.keys(aliases)
  for (let i = 0; i < canonicalKeys.length; i++) {
    const canonical = canonicalKeys[i]
    const list = Array.isArray(aliases[canonical]) ? aliases[canonical] : []
    for (let j = 0; j < list.length; j++) {
      const aliasCompact = String(list[j] || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '')
      if (aliasCompact && aliasCompact === compact) return canonical
    }
  }
  return raw
}

const resolveAltitudeReferenceType = function (mode, scope, ctx) {
  const normalizedMode = normalizeAltitudeMode(mode, ctx)
  const map = (scope === 'execute')
    ? _.get(ctx.ALTITUDE_RULES, 'executeModeReference', {})
    : _.get(ctx.ALTITUDE_RULES, 'templateModeReference', {})
  const fromPreset = map && typeof map === 'object' ? map[normalizedMode] : ''
  const fallbackMap = {
    EGM96: 'geoidLike',
    WGS84: 'absoluteEllipsoid',
    relativeToStartPoint: 'relativeToStartPoint',
    realTimeFollowSurface: 'relativeToSurface'
  }
  const resolved = String(fromPreset || fallbackMap[normalizedMode] || '').trim()
  return resolved || 'absoluteEllipsoid'
}

const getMissionAltitudeContextFor3d = function (missionState, ctx) {
  const ms = missionState || ctx.importMissionState || {}
  const extractFromFolderXml = function (folderEl) {
    if (!folderEl) return {}
    try {
      return ctx.fnXmlToObj(folderEl) || {}
    } catch (_error) {
      return {}
    }
  }
  const readFirstDomText = function (rootEl, tagNames) {
    if (!rootEl || !Array.isArray(tagNames)) return ''
    for (let i = 0; i < tagNames.length; i++) {
      const tagName = String(tagNames[i] || '').trim()
      if (!tagName) continue
      const node = rootEl.getElementsByTagName(tagName)[0]
      if (!node) continue
      const text = String(node.textContent || '').trim()
      if (text) return text
    }
    return ''
  }
  const templateFolderObj = extractFromFolderXml(ms.folderEl)
  const waylinesFolderObj = extractFromFolderXml(ms.secondaryFolderEl)
  const samplePoint = (ms && Array.isArray(ms.waypoints) && ms.waypoints[0]) ? ms.waypoints[0] : null
  const samplePointProps = (samplePoint && samplePoint.props && typeof samplePoint.props === 'object')
    ? samplePoint.props
    : {}
  const missionContextCarrier = {
    props: hasKmzAltitudeContextScopedProps(samplePoint)
      ? samplePointProps
      : applyKmzScopedAltitudeProps({}, buildKmzScopedAltitudePropsFromSources({
          templatePointProps: samplePoint && samplePoint.xmlEl && typeof ctx.fnXmlToObj === 'function'
            ? ctx.fnXmlToObj(samplePoint.xmlEl)
            : {},
          waylinePointProps: samplePoint && Array.isArray(samplePoint.linkedXmlEls) && samplePoint.linkedXmlEls[0] && typeof ctx.fnXmlToObj === 'function'
            ? ctx.fnXmlToObj(samplePoint.linkedXmlEls[0])
            : {},
          templateFolderProps: templateFolderObj,
          waylinesFolderProps: waylinesFolderObj
        }))
  }
  const resolvedMissionContext = resolveKmzMissionAltitudeContext(
    missionContextCarrier,
    'playback',
    {
      globalHeight: toFiniteNumber(readFirstDomText(ms.folderEl, ['wpml:globalHeight', 'globalHeight'])),
      heightMode: readFirstDomText(ms.folderEl, ['wpml:heightMode', 'heightMode']),
      executeHeightMode: readFirstDomText(ms.secondaryFolderEl, ['wpml:executeHeightMode', 'executeHeightMode'])
    }
  )
  const normalizedHeightMode = normalizeAltitudeMode(resolvedMissionContext.heightMode, ctx)
  const normalizedExecuteHeightMode = normalizeAltitudeMode(resolvedMissionContext.executeHeightMode, ctx)

  const pickFirstNumericFromPaths = function (obj, paths) {
    for (let i = 0; i < paths.length; i++) {
      const num = toFiniteNumber(_.get(obj, paths[i]))
      if (num !== null) return num
    }
    return null
  }
  const takeoffEllipsoidHeightDirect = pickFirstNumericFromPaths(templateFolderObj, [
    'wpml:takeOffRefPoint.wpml:ellipsoidHeight',
    'takeOffRefPoint.ellipsoidHeight',
    'wpml:takeoffRefPoint.wpml:ellipsoidHeight',
    'takeoffRefPoint.ellipsoidHeight'
  ])
    ?? pickFirstNumericFromPaths(waylinesFolderObj, [
      'wpml:takeOffRefPoint.wpml:ellipsoidHeight',
      'takeOffRefPoint.ellipsoidHeight',
      'wpml:takeoffRefPoint.wpml:ellipsoidHeight',
      'takeoffRefPoint.ellipsoidHeight'
    ])
    ?? pickFirstNumericFromPaths(samplePointProps, [
      'template.wpml:takeOffRefPoint.wpml:ellipsoidHeight',
      'template.takeOffRefPoint.ellipsoidHeight',
      'waylines.wpml:takeOffRefPoint.wpml:ellipsoidHeight',
      'waylines.takeOffRefPoint.ellipsoidHeight',
      'wpml:takeOffRefPoint.wpml:ellipsoidHeight',
      'takeOffRefPoint.ellipsoidHeight'
    ])

  return {
    globalHeight: resolvedMissionContext.globalHeight,
    heightMode: normalizedHeightMode,
    executeHeightMode: normalizedExecuteHeightMode,
    takeoffEllipsoidHeight: takeoffEllipsoidHeightDirect
  }
}

const resolvePointEllipsoidOffsetEstimate = function (point, absoluteHint, baseValue, ctx) {
  const absolute = toFiniteNumber(absoluteHint)
  const base = toFiniteNumber(baseValue)
  if (absolute !== null && base !== null) return absolute - base
  const cachedOffset = getPointCachedEgm96Offset(point)
  if (cachedOffset !== null) return cachedOffset
  return getConfiguredEgm96OffsetFallback(point, ctx)
}

const getPointSurfaceAbsoluteAltitudeFor3d = function (point, demMeters, options, ctx) {
  const dem = toFiniteNumber(demMeters)
  if (dem === null) return null
  const opts = (options && typeof options === 'object') ? options : {}
  const explicitSurfaceAbsolute = toFiniteNumber(opts.surfaceAbsoluteHint)
  if (explicitSurfaceAbsolute !== null) return explicitSurfaceAbsolute
  const offset = resolvePointEllipsoidOffsetEstimate(point, opts.absoluteHint, opts.baseValue, ctx)
  return offset === null ? dem : (dem + offset)
}

const getPointAltitudeFor3d = function (point, demMeters, missionAltContext, options, ctx) {
  const opts = _.assign({
    withMeta: false
  }, options || {})
  const props = (point && point.props && typeof point.props === 'object') ? point.props : {}
  const mCtx = missionAltContext || getMissionAltitudeContextFor3d(null, ctx)
  const dem = toFiniteNumber(demMeters)
  const heightMode = normalizeAltitudeMode((mCtx && mCtx.heightMode) || '', ctx)
  const executeHeightMode = normalizeAltitudeMode((mCtx && mCtx.executeHeightMode) || (mCtx && mCtx.heightMode) || '', ctx)
  const takeoffEllipsoidHeight = toFiniteNumber(mCtx && mCtx.takeoffEllipsoidHeight)
  const returnAltitude = function (value, meta) {
    if (!opts.withMeta) return value
    const safeValue = toFiniteNumber(value)
    const safeMeta = (meta && typeof meta === 'object') ? meta : {}
    return {
      altitude: safeValue,
      source: String(safeMeta.source || ''),
      refType: String(safeMeta.refType || ''),
      needsStartReference: safeMeta.needsStartReference === true
    }
  }

  const resolveAbsoluteFromMode = function (baseValue, mode, resolveOpts) {
    const numericBase = toFiniteNumber(baseValue)
    if (numericBase === null) {
      return {
        altitude: null,
        refType: '',
        needsStartReference: false
      }
    }
    const options = (resolveOpts && typeof resolveOpts === 'object') ? resolveOpts : {}
    const absoluteHint = toFiniteNumber(options.absoluteHint)
    const scope = String(options.scope || 'template')
    const refType = resolveAltitudeReferenceType(mode, scope === 'execute' ? 'execute' : 'template', ctx)
    if (refType === 'relativeToStartPoint') {
      if (takeoffEllipsoidHeight !== null) {
        return {
          altitude: takeoffEllipsoidHeight + numericBase,
          refType: refType,
          needsStartReference: false
        }
      }
      if (absoluteHint !== null) {
        return {
          altitude: absoluteHint,
          refType: refType,
          needsStartReference: false
        }
      }
      return {
        altitude: numericBase,
        refType: refType,
        needsStartReference: true
      }
    }
    if (refType === 'relativeToSurface') {
      if (absoluteHint !== null) {
        return {
          altitude: absoluteHint,
          refType: refType,
          needsStartReference: false
        }
      }
      const surfaceAbsolute = getPointSurfaceAbsoluteAltitudeFor3d(point, dem, {
        absoluteHint: absoluteHint,
        baseValue: numericBase
      }, ctx)
      if (surfaceAbsolute !== null) {
        return {
          altitude: surfaceAbsolute + numericBase,
          refType: refType,
          needsStartReference: false
        }
      }
      return {
        altitude: numericBase,
        refType: refType,
        needsStartReference: false
      }
    }
    if (refType === 'geoidLike') {
      if (absoluteHint !== null) {
        return {
          altitude: absoluteHint,
          refType: refType,
          needsStartReference: false
        }
      }
      const configuredOffset = resolvePointEllipsoidOffsetEstimate(point, absoluteHint, numericBase, ctx)
      if (configuredOffset !== null) {
        return {
          altitude: numericBase + configuredOffset,
          refType: refType,
          needsStartReference: false
        }
      }
      return {
        altitude: numericBase,
        refType: refType,
        needsStartReference: false
      }
    }
    return {
      altitude: numericBase,
      refType: refType,
      needsStartReference: false
    }
  }

  const pickFirstNumeric = function (paths) {
    for (let i = 0; i < paths.length; i++) {
      const v = toFiniteNumber(_.get(props, paths[i]))
      if (v !== null) return v
    }
    return null
  }
  const pickFirstBoolean = function (paths) {
    for (let i = 0; i < paths.length; i++) {
      let raw = _.get(props, paths[i])
      if (Array.isArray(raw)) raw = raw[0]
      if (raw && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, '_text')) {
        raw = raw._text
      }
      const text = String(raw === null || raw === undefined ? '' : raw).trim().toLowerCase()
      if (!text) continue
      if (text === '1' || text === 'true' || text === 'yes' || text === 'on') return true
      if (text === '0' || text === 'false' || text === 'no' || text === 'off') return false
      const num = Number(text)
      if (Number.isFinite(num)) return num > 0
    }
    return null
  }
  const parseBoolLike = function (rawValue) {
    let raw = rawValue
    if (Array.isArray(raw)) raw = raw[0]
    if (raw && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, '_text')) {
      raw = raw._text
    }
    const text = String(raw === null || raw === undefined ? '' : raw).trim().toLowerCase()
    if (!text) return null
    if (text === '1' || text === 'true' || text === 'yes' || text === 'on') return true
    if (text === '0' || text === 'false' || text === 'no' || text === 'off') return false
    const num = Number(text)
    if (Number.isFinite(num)) return num > 0
    return null
  }

  const useGlobalHeight = hasKmzAltitudeContextScopedProps(point)
    ? parseBoolLike(resolveKmzPlaybackUseGlobalHeight(point, null))
    : pickFirstBoolean([
        'waylines.wpml:useGlobalHeight',
        'waylines.useGlobalHeight',
        'wpml:useGlobalHeight',
        'useGlobalHeight',
        'template.wpml:useGlobalHeight',
        'template.useGlobalHeight'
      ])

  const pointExecuteHeight = pickFirstNumeric([
    'Point.executeHeight',
    'waylines.wpml:executeHeight',
    'waylines.executeHeight',
    'wpml:executeHeight',
    'executeHeight',
    'template.wpml:executeHeight',
    'template.executeHeight'
  ])
  const pointHeight = pickFirstNumeric([
    'Point.height',
    'waylines.wpml:height',
    'waylines.height',
    'wpml:height',
    'height',
    'template.wpml:height',
    'template.height'
  ])
  const pointEllipsoidHeight = pickFirstNumeric([
    'wpml:ellipsoidHeight',
    'ellipsoidHeight',
    'template.wpml:ellipsoidHeight',
    'waylines.wpml:ellipsoidHeight'
  ])
  const xmlExecuteHeight = getFirstNumericFromPointXmlTags(point, ['wpml:executeHeight', 'executeHeight'], ctx)
  const resolvedExecuteHeight = (pointExecuteHeight !== null) ? pointExecuteHeight : xmlExecuteHeight
  const globalHeight = toFiniteNumber(mCtx && mCtx.globalHeight)

  if (useGlobalHeight === true && globalHeight !== null) {
    const resolved = resolveAbsoluteFromMode(globalHeight, heightMode, { scope: 'template' })
    return returnAltitude(resolved.altitude, {
      source: 'globalHeight',
      refType: resolved.refType,
      needsStartReference: resolved.needsStartReference
    })
  }
  if (pointHeight !== null) {
    const resolved = resolveAbsoluteFromMode(pointHeight, heightMode, { absoluteHint: pointEllipsoidHeight, scope: 'template' })
    return returnAltitude(resolved.altitude, {
      source: 'pointHeight',
      refType: resolved.refType,
      needsStartReference: resolved.needsStartReference
    })
  }
  if (resolvedExecuteHeight !== null) {
    const resolved = resolveAbsoluteFromMode(resolvedExecuteHeight, executeHeightMode, { absoluteHint: pointEllipsoidHeight, scope: 'execute' })
    return returnAltitude(resolved.altitude, {
      source: 'executeHeight',
      refType: resolved.refType,
      needsStartReference: resolved.needsStartReference
    })
  }
  if (pointEllipsoidHeight !== null) {
    return returnAltitude(pointEllipsoidHeight, {
      source: 'ellipsoidHeight',
      refType: 'absoluteEllipsoid',
      needsStartReference: false
    })
  }

  const candidates = [
    _.get(props, 'coordinates.alt'),
    _.get(props, 'Point.coordinates.alt')
  ]
  for (let i = 0; i < candidates.length; i++) {
    const val = toFiniteNumber(candidates[i])
    if (val !== null) {
      return returnAltitude(val, {
        source: 'coordinatesAlt',
        refType: 'absoluteEllipsoid',
        needsStartReference: false
      })
    }
  }

  const xmlCoordinateAlt = getCoordinateAltFromPointXml(point, ctx)
  if (xmlCoordinateAlt !== null) {
    return returnAltitude(xmlCoordinateAlt, {
      source: 'xmlCoordinatesAlt',
      refType: 'absoluteEllipsoid',
      needsStartReference: false
    })
  }
  const xmlHeight = getFirstNumericFromPointXmlTags(point, ['wpml:height', 'height'], ctx)
  if (xmlHeight !== null) {
    const resolved = resolveAbsoluteFromMode(xmlHeight, heightMode, { absoluteHint: pointEllipsoidHeight, scope: 'template' })
    return returnAltitude(resolved.altitude, {
      source: 'xmlHeight',
      refType: resolved.refType,
      needsStartReference: resolved.needsStartReference
    })
  }
  if (xmlExecuteHeight !== null) {
    const resolved = resolveAbsoluteFromMode(xmlExecuteHeight, executeHeightMode, { absoluteHint: pointEllipsoidHeight, scope: 'execute' })
    return returnAltitude(resolved.altitude, {
      source: 'xmlExecuteHeight',
      refType: resolved.refType,
      needsStartReference: resolved.needsStartReference
    })
  }
  const xmlEllipsoidHeight = getFirstNumericFromPointXmlTags(point, ['wpml:ellipsoidHeight', 'ellipsoidHeight'], ctx)
  if (xmlEllipsoidHeight !== null) {
    return returnAltitude(xmlEllipsoidHeight, {
      source: 'xmlEllipsoidHeight',
      refType: 'absoluteEllipsoid',
      needsStartReference: false
    })
  }

  const directAltitude = toFiniteNumber(point && point.altitude)
  if (directAltitude !== null) {
    return returnAltitude(directAltitude, {
      source: 'pointAltitude',
      refType: 'absoluteEllipsoid',
      needsStartReference: false
    })
  }

  return returnAltitude(null, {
    source: '',
    refType: '',
    needsStartReference: false
  })
}

const recalculateEgm96EllipsoidForPoints = function (points, missionAltContext, ctx) {
  const list = Array.isArray(points) ? points : []
  if (!list.length) return 0
  const mCtx = missionAltContext || getMissionAltitudeContextFor3d(ctx.importMissionState, ctx)
  const heightMode = normalizeAltitudeMode((mCtx && mCtx.heightMode) || '', ctx)
  if (heightMode !== 'EGM96') return 0
  const heightAliases = getPointHeightPathAliases(ctx)
  const ellipsoidAliases = getPointEllipsoidHeightPathAliases(ctx)
  let changed = 0
  list.forEach((pt) => {
    const h = getPointNumericByPathAliases(pt, heightAliases)
    if (h === null) return
    const currentHe = getPointNumericByPathAliases(pt, ellipsoidAliases)
    const offset = getConfiguredEgm96OffsetFallback(pt, ctx)
    if (offset === null) return
    setPointCachedEgm96Offset(pt, offset)
    const nextHe = Math.round((h + offset) * 1000000) / 1000000
    if (currentHe !== null && Math.abs(currentHe - nextHe) < 1e-9) return
    if (setPointValueByPathAliases(pt, ellipsoidAliases, nextHe, ctx)) {
      syncPointCachedEgm96Offset(pt, ctx)
      changed++
    }
  })
  return changed
}

const convertAbsoluteAltitudeToModeValue = function (absoluteAltitude, point, mode, scope, missionAltContext, ctx) {
  const absolute = toFiniteNumber(absoluteAltitude)
  if (absolute === null) return null
  const mCtx = (missionAltContext && typeof missionAltContext === 'object') ? missionAltContext : {}
  const modeKey = String(mode || '').trim()
  const refType = resolveAltitudeReferenceType(modeKey, scope, ctx)
  if (refType === 'relativeToStartPoint') {
    const takeoff = toFiniteNumber(mCtx.takeoffEllipsoidHeight)
    return takeoff === null ? roundToTenth(absolute) : roundToTenth(absolute - takeoff)
  }
  if (refType === 'relativeToSurface') {
    const demMeters = getPreferredPointDemValue(point, ctx)
    if (demMeters !== null) return roundToTenth(absolute - demMeters)
    const projectHeight = toFiniteNumber(_.get(point, 'props.' + String(ctx.NON_EXPORT_POINT_HEIGHT_PATH || '')))
    return projectHeight === null ? roundToTenth(absolute) : roundToTenth(projectHeight)
  }
  if (refType === 'geoidLike') {
    const offset = getConfiguredEgm96OffsetFallback(point, ctx)
    return offset === null ? roundToTenth(absolute) : roundToTenth(absolute - offset)
  }
  return roundToTenth(absolute)
}

const syncPointAltitudeFields = function (point, options, ctx) {
  if (!point) return { changed: false, absoluteAltitude: null }
  const opts = (options && typeof options === 'object') ? options : {}
  const sourceMissionAltContext = (opts.sourceMissionAltContext && typeof opts.sourceMissionAltContext === 'object')
    ? opts.sourceMissionAltContext
    : (opts.targetMissionAltContext && typeof opts.targetMissionAltContext === 'object'
        ? opts.targetMissionAltContext
        : getMissionAltitudeContextFor3d(ctx.importMissionState, ctx))
  const targetMissionAltContext = (opts.targetMissionAltContext && typeof opts.targetMissionAltContext === 'object')
    ? opts.targetMissionAltContext
    : sourceMissionAltContext
  const sourceHeightOverride = toFiniteNumber(opts.sourceTemplateHeightOverride)
  const sourceExecuteHeightOverride = toFiniteNumber(opts.sourceExecuteHeightOverride)
  const absoluteAltitudeOverride = toFiniteNumber(opts.absoluteAltitudeOverride)

  let absoluteAltitude = absoluteAltitudeOverride
  if (absoluteAltitude === null && sourceHeightOverride !== null) {
    const clonedProps = _.cloneDeep(point.props || {})
    getPointHeightPathAliases(ctx).forEach((path) => {
      _.set(clonedProps, path, sourceHeightOverride)
    })
    getPointEllipsoidHeightPathAliases(ctx).forEach((path) => {
      _.unset(clonedProps, path)
    })
    absoluteAltitude = getPointAltitudeFor3d(
      _.assign({}, point, {
        props: clonedProps
      }),
      getPreferredPointDemValue(point, ctx),
      sourceMissionAltContext,
      null,
      ctx
    )
  }
  if (absoluteAltitude === null && sourceExecuteHeightOverride !== null) {
    const clonedProps = _.cloneDeep(point.props || {})
    getPointHeightPathAliases(ctx).forEach((path) => {
      _.unset(clonedProps, path)
    })
    getPointExecuteHeightPathAliases(ctx).forEach((path) => {
      _.set(clonedProps, path, sourceExecuteHeightOverride)
    })
    getPointEllipsoidHeightPathAliases(ctx).forEach((path) => {
      _.unset(clonedProps, path)
    })
    absoluteAltitude = getPointAltitudeFor3d(
      _.assign({}, point, { props: clonedProps }),
      getPreferredPointDemValue(point, ctx),
      sourceMissionAltContext,
      null,
      ctx
    )
  }
  if (absoluteAltitude === null) {
    absoluteAltitude = getPointAltitudeFor3d(point, getPreferredPointDemValue(point, ctx), sourceMissionAltContext, null, ctx)
  }
  const absolute = toFiniteNumber(absoluteAltitude)
  if (absolute === null) return { changed: false, absoluteAltitude: null }

  const nextHeight = convertAbsoluteAltitudeToModeValue(
    absolute,
    point,
    normalizeAltitudeMode((targetMissionAltContext && targetMissionAltContext.heightMode) || '', ctx),
    'template',
    targetMissionAltContext,
    ctx
  )
  const nextExecuteHeight = convertAbsoluteAltitudeToModeValue(
    absolute,
    point,
    normalizeAltitudeMode((targetMissionAltContext && (targetMissionAltContext.executeHeightMode || targetMissionAltContext.heightMode)) || '', ctx),
    'execute',
    targetMissionAltContext,
    ctx
  )
  const nextEllipsoidHeight = Math.round(absolute * 1000000) / 1000000

  let changed = false
  if (nextHeight !== null && setPointValueByPathAliases(point, getPointHeightPathAliases(ctx), nextHeight, ctx)) {
    changed = true
  }
  if (nextExecuteHeight !== null && setPointValueByPathAliases(point, getPointExecuteHeightPathAliases(ctx), nextExecuteHeight, ctx)) {
    changed = true
  }
  if (setPointValueByPathAliases(point, getPointEllipsoidHeightPathAliases(ctx), nextEllipsoidHeight, ctx)) {
    changed = true
  }

  if (!point.props || typeof point.props !== 'object') point.props = {}
  if (!point.props.coordinates || typeof point.props.coordinates !== 'object') point.props.coordinates = {}
  point.altitude = roundToTenth(absolute)
  point.props.coordinates.alt = point.altitude
  if (typeof ctx.fnSetByPath === 'function') {
    ctx.fnSetByPath(point.props, 'Point.coordinates.alt', point.altitude)
  }
  if (typeof ctx.fnUpdatePointXmlByPath === 'function') {
    ctx.fnUpdatePointXmlByPath(point, 'coordinates.alt', point.altitude)
  }
  if (typeof ctx.fnTemplateSupportsPointCoordinatesField === 'function' &&
      ctx.fnTemplateSupportsPointCoordinatesField() &&
      typeof ctx.fnBuildPointCoordinatesText === 'function') {
    const pointCoordinatesText = ctx.fnBuildPointCoordinatesText(point.props || {})
    if (typeof ctx.fnSetByPath === 'function') {
      ctx.fnSetByPath(point.props, 'Point.coordinates', pointCoordinatesText)
    }
    if (typeof ctx.fnUpdatePointXmlByPath === 'function') {
      ctx.fnUpdatePointXmlByPath(point, 'Point.coordinates', pointCoordinatesText)
    }
  }
  syncPointCachedEgm96Offset(point, ctx)

  return {
    changed: changed,
    absoluteAltitude: absolute,
    height: nextHeight,
    executeHeight: nextExecuteHeight,
    ellipsoidHeight: nextEllipsoidHeight
  }
}

const syncPointsAltitudeFields = function (points, options, ctx) {
  const list = Array.isArray(points) ? points : []
  let changedCount = 0
  const results = list.map((point) => {
    const result = syncPointAltitudeFields(point, options, ctx)
    if (result && result.changed) changedCount += 1
    return result
  })
  return {
    changedCount: changedCount,
    results: results
  }
}

const setPointDemValue = function (point, providerKey, elevationMeters) {
  if (!point || !providerKey || !Number.isFinite(Number(elevationMeters))) return
  if (!point.props || typeof point.props !== 'object') point.props = {}
  if (!point.props.__demValues || typeof point.props.__demValues !== 'object') {
    point.props.__demValues = {}
  }
  point.props.__demValues[providerKey] = Math.round(Number(elevationMeters) * 10) / 10
}

const getPointAglValues = function (point) {
  const values = _.get(point, 'props.__aglValues')
  if (!values || typeof values !== 'object') return {}
  return values
}

const setPointAglValue = function (point, providerKey, altitudeMeters, aglMeters) {
  if (!point || !providerKey) return
  if (!Number.isFinite(Number(altitudeMeters)) || !Number.isFinite(Number(aglMeters))) return
  if (!point.props || typeof point.props !== 'object') point.props = {}
  if (!point.props.__aglValues || typeof point.props.__aglValues !== 'object') {
    point.props.__aglValues = {}
  }
  point.props.__aglValues[providerKey] = {
    altitudeMeters: Math.round(Number(altitudeMeters) * 10) / 10,
    aglMeters: Math.round(Number(aglMeters) * 10) / 10
  }
}

const getPointHeightValue = function (point, ctx) {
  if (!point) return null
  const dem = getPreferredPointDemValue(point, ctx)
  const mCtx = getMissionAltitudeContextFor3d(ctx.importMissionState, ctx)
  const resolved = getPointAltitudeFor3d(point, dem, mCtx, null, ctx)
  if (resolved !== null && Number.isFinite(Number(resolved))) {
    return Number(resolved)
  }
  const props = point.props
  const fallbackCandidates = [
    point.altitude,
    _.get(props, 'coordinates.alt'),
    _.get(props, 'Point.coordinates.alt'),
    _.get(props, 'Point.executeHeight'),
    _.get(props, 'wpml:executeHeight'),
    _.get(props, 'executeHeight'),
    _.get(props, 'template.wpml:executeHeight'),
    _.get(props, 'waylines.wpml:executeHeight'),
    _.get(props, 'Point.height'),
    _.get(props, 'wpml:height'),
    _.get(props, 'height'),
    _.get(props, 'template.wpml:height'),
    _.get(props, 'waylines.wpml:height')
  ]
  for (let i = 0; i < fallbackCandidates.length; i++) {
    const n = Number(fallbackCandidates[i])
    if (Number.isFinite(n)) return n
  }
  return null
}

const getPointRawHeightValue = function (point, ctx) {
  if (!point) return null
  const props = point.props
  const readFiniteHeight = function (raw) {
    return toFiniteNumber(raw)
  }
  const candidates = [
    _.get(props, 'coordinates.alt'),
    _.get(props, 'Point.coordinates.alt'),
    _.get(props, 'Point.executeHeight'),
    _.get(props, 'wpml:executeHeight'),
    _.get(props, 'executeHeight'),
    _.get(props, 'template.wpml:executeHeight'),
    _.get(props, 'waylines.wpml:executeHeight'),
    _.get(props, 'Point.height'),
    _.get(props, 'wpml:height'),
    _.get(props, 'height'),
    _.get(props, 'template.wpml:height'),
    _.get(props, 'waylines.wpml:height'),
    point.altitude
  ]
  for (let i = 0; i < candidates.length; i++) {
    const n = readFiniteHeight(candidates[i])
    if (n !== null) return n
  }
  const xmlExecuteHeight = getFirstNumericFromPointXmlTags(point, ['wpml:executeHeight', 'executeHeight'], ctx)
  if (xmlExecuteHeight !== null) return xmlExecuteHeight
  const xmlHeight = getFirstNumericFromPointXmlTags(point, ['wpml:height', 'height'], ctx)
  if (xmlHeight !== null) return xmlHeight
  const xmlEllipsoidHeight = getFirstNumericFromPointXmlTags(point, ['wpml:ellipsoidHeight', 'ellipsoidHeight'], ctx)
  if (xmlEllipsoidHeight !== null) return xmlEllipsoidHeight
  return null
}

const applyPointHeightValue = function (point, heightValue, missionAltContext, ctx) {
  const h = Number(heightValue)
  if (!point || !Number.isFinite(h)) return false
  const result = syncPointAltitudeFields(point, {
    sourceMissionAltContext: missionAltContext,
    targetMissionAltContext: missionAltContext,
    sourceTemplateHeightOverride: h
  }, ctx)
  return !!(result && (result.changed || result.absoluteAltitude !== null))
}

const getWaypointTooltipText = function (point, ctx) {
  const name = ctx.fnGetPointName(point).trim()
  const description = ctx.fnGetPointDescription(point).trim()
  const idx = (point && typeof point.idx === 'number') ? point.idx + 1 : '?'
  const base = name || ('WP ' + idx)
  const heightVal = getPointHeightValue(point, ctx)
  const heightPrefix = (heightVal === null) ? '' : ('(' + formatMeters(heightVal) + ' m) ')
  if (!description) return heightPrefix + base
  if (description === base) return heightPrefix + base
  return heightPrefix + base + ' ' + description
}

export {
  buildDemLine,
  formatMeters,
  buildAglLine,
  getPointDemValues,
  getPreferredPointDemValue,
  getFirstNumericFromPointXmlTags,
  getCoordinateAltFromPointXml,
  getPointNumericByPathAliases,
  setPointValueByPathAliases,
  computeDefaultEgm96OffsetForPoints,
  getPointCachedEgm96Offset,
  setPointCachedEgm96Offset,
  syncPointCachedEgm96Offset,
  isEllipsoidHeightPath,
  isWaypointHeightPath,
  normalizeAltitudeMode,
  resolveAltitudeReferenceType,
  getPointSurfaceAbsoluteAltitudeFor3d,
  getMissionAltitudeContextFor3d,
  getPointAltitudeFor3d,
  recalculateEgm96EllipsoidForPoints,
  syncPointAltitudeFields,
  syncPointsAltitudeFields,
  setPointDemValue,
  getPointAglValues,
  setPointAglValue,
  getPointHeightValue,
  getPointRawHeightValue,
  applyPointHeightValue,
  getWaypointTooltipText
}
